import { useEffect, useRef, useState } from 'react';
import { pushCallDebugLog } from 'vcs-common';
import { VideoQualityMonitor } from '../../videoQualityMonitor';
const POOR_MAX_BITRATE = 450000;
const GOOD_MAX_BITRATE = 1200000;
const MAX_FRAMERATE = 24;
const readNumber = (stat, key) => Number(stat?.[key] ?? 0);
const isVideoStat = (stat, type) => stat.type === type && (stat.kind === 'video' || stat.mediaType === 'video');
/**
 * Следит за качеством исходящего/входящего видео в 1:1 и снижает лимит битрейта,
 * пока связь плохая. Возвращает состояние для индикатора качества связи.
 */
export function useVideoQualityMonitor({ room, isGroupConsultation, isScreenSharing, localVideoTrackRef, }) {
    const monitorRef = useRef(new VideoQualityMonitor());
    const countersRef = useRef({});
    const videoProfileRef = useRef('540p');
    const videoProfileTransitionRef = useRef(Promise.resolve());
    const [connectionQuality, setConnectionQuality] = useState('GOOD');
    useEffect(() => {
        if (isGroupConsultation)
            return;
        const delta = (key, value) => {
            const previous = countersRef.current[key];
            countersRef.current[key] = value;
            return previous === undefined ? 0 : Math.max(0, value - previous);
        };
        const collect = async () => {
            const publisher = room.engine.pcManager?.publisher?._pc;
            const subscriber = room.engine.pcManager?.subscriber?._pc;
            if (!publisher && !subscriber)
                return;
            const reports = await Promise.all([publisher?.getStats(), subscriber?.getStats()]);
            const all = reports.flatMap((report) => (report ? Array.from(report.values()) : []));
            const pair = all.find((stat) => stat.type === 'candidate-pair' &&
                (stat.selected || (stat.nominated && stat.state === 'succeeded')));
            const outbound = all.find((stat) => isVideoStat(stat, 'outbound-rtp'));
            const remoteInbound = all.find((stat) => isVideoStat(stat, 'remote-inbound-rtp'));
            const inbound = all.find((stat) => isVideoStat(stat, 'inbound-rtp'));
            const packetsLost = delta('lost', readNumber(remoteInbound ?? inbound, 'packetsLost'));
            const packetsReceived = delta('received', readNumber(remoteInbound ?? inbound, 'packetsReceived'));
            const lossSource = (remoteInbound ?? inbound);
            const sample = {
                rttMs: Number.isFinite(Number(pair?.currentRoundTripTime))
                    ? Number(pair.currentRoundTripTime) * 1000
                    : null,
                jitterMs: Number.isFinite(Number(lossSource?.jitter)) ? Number(lossSource.jitter) * 1000 : null,
                lossFraction: typeof remoteInbound?.fractionLost === 'number'
                    ? Number(remoteInbound.fractionLost)
                    : packetsLost + packetsReceived > 0
                        ? packetsLost / (packetsLost + packetsReceived)
                        : null,
                framesSentPerSecond: outbound ? delta('framesSent', readNumber(outbound, 'framesSent')) : null,
                framesDecodedPerSecond: inbound ? delta('framesDecoded', readNumber(inbound, 'framesDecoded')) : null,
                nackDelta: delta('nack', readNumber(remoteInbound ?? inbound, 'nackCount')),
                pliDelta: delta('pli', readNumber(remoteInbound ?? inbound, 'pliCount')),
                rtxDelta: delta('rtx', readNumber(outbound, 'retransmittedPacketsSent')),
                freezeCountDelta: delta('freezes', readNumber(inbound, 'freezeCount')),
                freezeDurationDeltaMs: delta('freezeDuration', readNumber(inbound, 'totalFreezesDuration')) * 1000,
                hasExpectedRemoteVideo: room.remoteParticipants.size > 0 && Boolean(inbound),
            };
            const snapshot = monitorRef.current.add(sample);
            if (snapshot.changed) {
                setConnectionQuality(snapshot.state);
                pushCallDebugLog('quality', `Качество видеосвязи: ${snapshot.state}`, { ...sample }, snapshot.state === 'POOR' ? 'warn' : 'info');
            }
            const targetProfile = snapshot.state === 'POOR' ? '360p' : snapshot.state === 'GOOD' ? '540p' : videoProfileRef.current;
            const track = localVideoTrackRef.current;
            if (!track || track.isMuted || isScreenSharing || targetProfile === videoProfileRef.current)
                return;
            videoProfileTransitionRef.current = videoProfileTransitionRef.current
                .catch(() => undefined)
                .then(async () => {
                const maxBitrate = targetProfile === '360p' ? POOR_MAX_BITRATE : GOOD_MAX_BITRATE;
                const sender = track.sender;
                const parameters = sender?.getParameters();
                if (!sender || !parameters?.encodings || parameters.encodings.length !== 1) {
                    pushCallDebugLog('quality', 'Не удалось изменить video profile: ожидается один sender encoding', undefined, 'warn');
                    return;
                }
                try {
                    parameters.encodings[0].maxBitrate = maxBitrate;
                    parameters.encodings[0].maxFramerate = MAX_FRAMERATE;
                    await sender.setParameters(parameters);
                    videoProfileRef.current = targetProfile;
                    pushCallDebugLog('quality', `Лимит исходящего видео изменён на ${targetProfile}`, {
                        maxBitrate,
                        maxFramerate: MAX_FRAMERATE,
                    });
                }
                catch (error) {
                    pushCallDebugLog('quality', 'Не удалось изменить camera profile', { error: String(error) }, 'error');
                }
            });
        };
        const timer = setInterval(() => void collect().catch((error) => console.warn('Quality monitor stats failed', error)), 1000);
        return () => clearInterval(timer);
    }, [isGroupConsultation, isScreenSharing, localVideoTrackRef, room]);
    return connectionQuality;
}
